import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, MapPin, Mail, Phone, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    date: '',
    message: '',
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({
        title: 'Almost there',
        description: 'Please complete name, email, and message.',
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address so we can reply.',
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/contact`, form, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 25000,
      });

      toast({
        title: 'Enquiry received',
        description:
          data?.message ||
          'Thank you. Our atelier will be in touch within 24 hours.',
      });
      setForm({ name: '', email: '', phone: '', eventType: '', date: '', message: '' });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        'Something went wrong sending your enquiry. Please try again or email info@dionmagee.com directly.';
      toast({
        title: 'Unable to send',
        description: detail,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full bg-transparent border-b border-white/15 focus:border-[#c9a96e] py-4 text-white placeholder:text-white/30 outline-none text-sm tracking-wide transition-colors disabled:opacity-50';

  return (
    <section id="contact" className="relative bg-[#0a0a0a] py-28 lg:py-40 px-6 lg:px-12">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-20">
        <div>
          <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">Get In Touch</p>
          <h2 className="font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            Begin Your
          </h2>
          <h2
            className="font-serif italic text-[#c9a96e] text-5xl md:text-6xl lg:text-7xl leading-[1.05] -mt-1"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Story
          </h2>

          <p className="text-white/65 mt-8 leading-[1.9] font-light max-w-md text-[15px]">
            We accept a limited number of celebrations each year. Share a few details about your vision and our atelier will respond personally within 24 hours.
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4 text-white/80">
              <MapPin className="text-[#c9a96e] mt-1" size={16} strokeWidth={1.5} />
              <div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-1">Atelier Offices</div>
                <div className="font-light">New York · Florida · Globally on Request</div>
              </div>
            </div>
            <div className="flex items-start gap-4 text-white/80">
              <Mail className="text-[#c9a96e] mt-1" size={16} strokeWidth={1.5} />
              <div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-1">Enquiries</div>
                <div className="font-light">info@dionmagee.com</div>
              </div>
            </div>
            <div className="flex items-start gap-4 text-white/80">
              <Phone className="text-[#c9a96e] mt-1" size={16} strokeWidth={1.5} />
              <div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-1">Concierge</div>
                <div className="font-light">+1 888-299-2250</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-2">
          <div className="grid sm:grid-cols-2 gap-6">
            <input name="name" value={form.name} onChange={onChange} placeholder="Full Name" className={inputCls} disabled={submitting} />
            <input name="email" value={form.email} onChange={onChange} type="email" placeholder="Email Address" className={inputCls} disabled={submitting} />
          </div>
          <div className="grid sm:grid-cols-2 gap-6 pt-2">
            <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone (optional)" className={inputCls} disabled={submitting} />
            <input name="eventType" value={form.eventType} onChange={onChange} placeholder="Event Type" className={inputCls} disabled={submitting} />
          </div>
          <div className="pt-2">
            <input name="date" value={form.date} onChange={onChange} placeholder="Estimated Date" className={inputCls} disabled={submitting} />
          </div>
          <div className="pt-2">
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              rows={5}
              placeholder="Tell us about your vision..."
              className={`${inputCls} resize-none`}
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="group mt-10 inline-flex items-center gap-3 bg-[#e8d5a8] hover:bg-[#c9a96e] text-black px-10 py-4 text-[11px] tracking-[0.35em] uppercase transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sending Enquiry
              </>
            ) : (
              <>
                Submit Enquiry
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
